import { useTranslation } from "react-i18next";

import { Block, Image, Touchable, Typography, palette, spacing, useBreakpoints } from "@stepapp/ui";

import { ApartmentType } from "@stepapp/core/apiTypes";
import React, { useCallback } from "react";
import { UseFormSetValue } from "react-hook-form";
import { StyleSheet } from "react-native";
import { block, house } from "../../assets/images";
import { ReservationFormParams } from "../../store/draftReservation/types";
import ErrorMessage from "../ErrorMessage";
import { Grid } from "../Grid";
import { ServiceSceneDescription, ServiceSceneHeader } from "./ServiceSceneHeader";

type Props = {
  errorMsg?: string;
  setValue: UseFormSetValue<ReservationFormParams>;
  homeType: ApartmentType | string;
};

const imageMapping = {
  [ApartmentType.Apartment]: block,
  [ApartmentType.House]: house,
} as const;

const HomeTypeQuestion = ({ errorMsg, homeType, setValue }: Props) => {
  const { t } = useTranslation();
  const { sm } = useBreakpoints();

  const handleChangeHomeType = useCallback(
    (type: ApartmentType) => {
      setValue("address.type", type, {
        shouldValidate: true,
      });
      setValue("meta.homeTypePlaceholder", type, {
        shouldValidate: true,
      });
    },
    [setValue],
  );

  return (
    <Block marginTop={1}>
      <ServiceSceneHeader name={t("screens.accommodation.title")} />
      <ServiceSceneDescription
        marginBottom={2}
        description={t("screens.accommodation.description")}
      />

      <Grid columns={sm ? 2 : 1} gap={spacing.unit * (sm ? 2 : 0)}>
        {Object.values(ApartmentType).map((type) => (
          <Touchable
            key={type}
            onPress={() => handleChangeHomeType(type)}
            style={[
              styles.homeTypeButton,
              type === homeType && { backgroundColor: palette.primary.accent },
            ]}
          >
            <Block style={styles.imageContainer}>
              <Image
                contentFit="contain"
                height={spacing.unit * 5}
                source={imageMapping[type]}
                width={spacing.unit * 5}
              />
            </Block>

            <Typography variant="button" type={type === homeType ? "white" : "primary"}>
              {t("screens.accommodation.type", { context: type })}
            </Typography>
          </Touchable>
        ))}
      </Grid>

      <ErrorMessage message={errorMsg} />
    </Block>
  );
};

export default React.memo(HomeTypeQuestion);

const styles = StyleSheet.create({
  homeTypeButton: {
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
    marginVertical: spacing.unit,
    paddingHorizontal: spacing.unit * 2,
    paddingVertical: spacing.unit * 1.75,
    flexDirection: "row",
    alignItems: "center",
  },
  imageContainer: {
    marginRight: spacing.unit * 3.75,
    padding: spacing.unit * 1,
    backgroundColor: palette.common.lightGray,
    borderRadius: spacing.radius,
  },
});
